import React, { Component } from 'react';
import { ProductTable, Zip, ProductRow, Message, ShippingTable } from '@developers/cart';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import Button from '@mui/material/Button';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Spinner from '@developers/ui/Spinner';
import { useAuth } from '@developers/single-sign-on';
import OrderSummary  from '@developers/checkout/Review/OrderSummary';
import SaveQuotePopup from '../../components/SaveQuotePopup';
import ProblemPartsPopup from '../../components/ProblemPartsPopup';
import globalStyles from '../../styles';
import { usePermission } from '../../hooks/usePermission';
import { hasPermission } from '../../utils/hasPermission';
import { changePageTitle } from '../../utils/changePageTitle';
import Analytics from '../../utils/Analytics';
import { useScreenSize } from '../../hooks/useScreenSize';
import useScreenResize from '../../hooks/useScreenResize';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { cartOperations, cartSelectors } from '../../redux/cart';
import { checkoutOperations } from '../../redux/checkout';
import { useRouter } from 'next/router';
import { useHeaderHeight } from '../../hooks/useHeaderHeight';
import Signature from '@developers/checkout/Shipping/Signature';
import * as accountsAjax from '../../ajax/accounts';
import * as cartAjax from '../../ajax/cart';
import Render from '../../components/Render';
import CartHistory from '../../components/CartHistory';
import { useAjax } from '@developers/use-ajax';

const mapStateToProps = function(state) {
  return {
    cart: state.get('cart'),
  }
};

const mapDispatchToProps = function (dispatch) {
  return bindActionCreators({
    getCart: cartOperations.getCart,
    updateCart: cartOperations.updateCart,
    addToCart: cartOperations.addToCart,
    updateCartCount: cartOperations.updateCartCount,
    addToCartAndRedirect: cartOperations.addToCartAndRedirect,
    removeItem: cartOperations.removeItem,
    updateZip: cartOperations.updateZip,
    updateSelectedShipping: cartOperations.updateSelectedShipping,
    clearRemovedProducts: cartOperations.clearRemovedProducts,
    getCoupon: cartOperations.getCoupon,
    removeCoupon: cartOperations.removeCoupon,
    goToCheckout: cartOperations.goToCheckout,
    setAdminCheckout: cartOperations.setAdminCheckout,
    clearAlerts: cartOperations.clearAlerts,
    setPurchaseTakenBy: checkoutOperations.setPurchaseTakenBy,
    saveQuote: cartOperations.saveQuote,
    rewriteCart: cartOperations.rewriteCart,
    alertAction: cartOperations.alertAction,
    requireSignature: cartOperations.requireSignature,
    alertAction: cartOperations.alertAction,
    clearAlerts: cartOperations.clearAlerts,
  }, dispatch)
};

const getStyles = (headerHeight) => ({
  buttonStyleMobile: {
    position: 'fixed',
    top: headerHeight,
    left: 0,
    zIndex: 999,
    width: '100%',
    marginTop: 0,
    marginBottom: 1 
  },
  buttonStyleDesktop: { 
    width: '100%',
    marginTop: 15,
    marginBottom: 15
  }
});

const CartRouter = (props) => {
  const auth = useAuth();
  const router = useRouter();;
  const { size } = useScreenSize();
  const headerHeight = useHeaderHeight();

  // Don't actually need these variables. Just need the page to rerender if the screen size changes after the initial render.
  const [width, height] = useScreenResize();
  
  const [accountCookiePresent, setAccountCookiePresent] = React.useState(false);
  const [cartHistory, getCartHistory, getCartHistoryMeta] = useAjax(accountsAjax.getCartHistory, []);
  const [discountLevel, getDiscountLevel, getDiscountLevelMeta] = useAjax(accountsAjax.getDiscountLevel, '');
  const [updatedCart, addPartToCart, addPartToCartMeta] = useAjax(cartAjax.addProductToCart, {});

  React.useEffect(() => {
    const account_info_cookie = document.cookie.replace(/(?:(?:^|.*;\s*)account_info\s*\=\s*([^;]*).*$)|^.*$/, "$1");
    
    if (account_info_cookie) {
      setAccountCookiePresent(true);
      const account_info_array = decodeURIComponent(account_info_cookie).split('|')

      const account_id = account_info_array[0];
      getCartHistory(parseInt(account_id));
      getDiscountLevel(parseInt(account_id));
    }
  }, []);

  const findProductByNumber = (number) => {
    return cartSelectors.findProductByNumber(number, props.cart.get('products'));
  }

  function changePage(page) {
    router.push(page);
  }
  
  return (
    <Cart 
      {...props} 
      findProductByNumber={findProductByNumber} 
      query={router.query} 
      auth={auth} 
      size={size} 
      changePage={changePage} 
      headerHeight={headerHeight} 
      width={width}
      accountCookiePresent={accountCookiePresent}
      getCartHistory={getCartHistory}
      getCartHistoryMeta={getCartHistoryMeta}
      getDiscountLevel={getDiscountLevel}
      cart_history={cartHistory}
      discount_level={discountLevel}
      addPartToCart={addPartToCart}
      addPartToCartMeta={addPartToCartMeta}
    />
  );
}

class Cart extends Component {

  constructor(props) {
    super(props);

    this.state = { 
      cookie_present: false,
      cart_history: [],
      discount_level: ''
    };
  }

  async componentDidMount() {
    this.props.clearRemovedProducts();
    changePageTitle('Cart');

    /**
     * IS THIS USED?? I CAN TRACE WHEN IT WAS ADDED, BUT NOT WHY?
     * 
     * Above is about the rewriteCart functionality. It also exists in checkout
     */
    if (this.props.query.update && this.props.query.update != '') {
      let options = {};
      if (this.props.query.zip && this.props.query.zip != '') {
        options.zip = this.props.query.zip;
      }
      if (this.props.query['ship-code'] && this.props.query['ship-code'] != '') {
        options['ship-code'] = this.props.query['ship-code'];
      }
      this.props.rewriteCart(this.props.query.update, options);
    } else {
      this.props.getCart(this.props.auth.session_data.admin);
    }
  }

  componentDidUpdate(prevProps) {
    // If we have a pending order now and we did not have one before
    // consider this a new checkout event
    // Fire off Analytics code to start checkout
    if (!this.props.cart.get('loading') && !prevProps.cart.get('pending') && this.props.cart.get('pending')) {
      Analytics.items(this.props.cart.get('products')).beginCheckout();
    }
  }

  updateCart = (productNumber, qty, index = false) => {
    this.props.updateCart(productNumber, qty, index, this.props.updateCartCount);
  }

  removeItem = (number, index) => {
    const product = this.props.findProductByNumber(number);
    this.props.removeItem(number, product, index, this.props.updateCartCount);
  }

  renderTotalSavings = (savings) => {
    if (parseFloat(savings) <= 0) {
      return null;
    }
  
    return (
      <div style={{ fontWeight: 'bold' }}>Total Savings: ${savings}</div>
    );
  }

  render() {
    const { cart, auth, updateZip, updateSelectedShipping, size } = this.props;
    const couponCode = cart.get('coupon') ? cart.getIn(['coupon', 'special_coupon_id']):'';
    const mobile = size === 'xxs' || size === 'xs' || size === 'sm' ? true : false;
    const styles = getStyles(this.props.headerHeight);
    const showRegularCheckout = !auth.session_data.admin || (auth.session_data.admin && hasPermission(auth.session_data.user?.permissions, 'use-regular-checkout'));

    let display = '';
    if (auth.session_data.admin && hasPermission(auth.session_data.user?.permissions, 'use-regular-checkout')) {
      display = 'none';
    }
    
    if (cart.get('loading')) {
      return <Spinner show={true} />
    }

    if (cart.get('products').count() <= 0) {
      return (
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <h1><b>Your shopping cart is empty</b></h1>
            </div>
          </div>
          <div className='row'>
            <Render if={this.props.accountCookiePresent}>
              <CartHistory
                data={this.props.cart_history}
                meta={this.props.getCartHistoryMeta}
                screenWidth={this.props.width}
                discountLevel={this.props.discount_level}
                addProductToCart={this.updateCart}
                addProductToCartMeta={this.props.addPartToCartMeta}
                hideCartLink={true}
              />
            </Render>
          </div>
        </div>
      );
    }

    return (
      <div className={this.props.classes.globalstyle}>
        <ProblemPartsPopup alerts={cart.get('alerts')} alertAction={this.props.alertAction} clearAlerts={this.props.clearAlerts} />
        <div className="container-fluid">
          <div className="row">
            <div className='col-sm-4 col-sm-push-8' style={ mobile ? { marginTop: 50 } : {}}>
              {cart.get('messages').map((message, index) => (
                <Message key={index} text={message.get('text')} type={message.get('type')} />
              ))}
              {showRegularCheckout ?
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>  this.props.goToCheckout(false, this.props.changePage)}
                    style={mobile ? styles.buttonStyleMobile : styles.buttonStyleDesktop}
                    endIcon={<ChevronRight />}
                  >
                    Proceed To Checkout
                  </Button>
                </div>
                : null
              }
              <AdminButtons {...this.props} />
              <OrderSummary cart={cart} count={cart.getIn(['product_count', 'count'])} couponCode={couponCode} />
              <div style={{marginTop: '10px'}}>
                <div className='row'>
                  <div className="col-sm-12">
                    <Zip zip={cart.get('zip')} handleUpdate={updateZip} loading={cart.get('zipLoading')} />
                  </div>
                </div>
                <div className='row'>
                  <div className="col-sm-12">
                    <Signature
                      cost={cart.getIn(['shipping', 'signature']).toJS()?.price}
                      checked={cart.getIn(['shipping', 'signature']).toJS()?.required}
                      requireSignature={this.props.requireSignature}
                      condensed={false}
                    />
                    <ShippingTable
                      count={cart.getIn(['shipping', 'rates']).count()}
                      selected={cart.getIn(['selected_shipping', 'id'])}
                      updateSelectedShipping={updateSelectedShipping}
                      loading={cart.get('zipLoading')}
                      options={cart.getIn(['shipping', 'rates'])}
                      signature={cart.getIn(['shipping', 'signature']).toJS()}
                      requireSignature={this.props.requireSignature}
                      condensed={false}
                    >
                      {cart.getIn(['shipping', 'rates']).map(option => {
                        return (
                        <ListItem
                          button
                          key={option.get('id')}
                          disabled={cart.get('zipLoading')}
                          value={option.get('id')}
                          selected={cart.getIn(['selected_shipping', 'id']) == option.get('id')}
                          style={{ display: 'block' }}
                          onClick={() => updateSelectedShipping(option)}
                        >
                          <Typography variant="body1" style={{ fontWeight: 'bold', color: 'green', display: 'block' }}>{option.get('desc')}</Typography>
                          <Typography variant="body1" style={{ display: 'block' }}>${option.get('rate')} - {option.get('estimate')}</Typography>
                        </ListItem>
                        );
                      })}
                    </ShippingTable>
                  </div>
                </div>
                <div className='row' style={{ display: display }}>
                  <div className='col-sm-12' style={{ display: mobile ? 'none' : '' }}>
                    {showRegularCheckout ?
                      <div>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>  this.props.goToCheckout(false, this.props.changePage)}
                            style={mobile ? styles.buttonStyleMobile : styles.buttonStyleDesktop}
                            endIcon={<ChevronRight />}
                        >
                            Proceed To Checkout
                        </Button>
                      </div>
                      : null
                    }
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-8 col-sm-pull-4">

              <h1 style={{ textAlign: 'left' }}><b>Items in your Cart</b></h1><hr style={{ marginTop: '0px' }} />
              <ProductTable>
                {cart.get('products').map((product, index) => {
                  const special = product.getIn(['cart', 'special']) ? { special: product.getIn(['cart', 'special']).toJS() } : {};
                  const file = { path: '', 
                    thumbnail: product.get('file_medium')
                  };
                  return (
                    <ProductRow
                      key={product.get('product_id')}
                      index={index}
                      removeItem={this.removeItem}
                      updateCart={this.updateCart}
                      loading={cart.get('productLoading')}
                      removed={product.get('removed')}
                      file={file}
                      price={product.getIn(['cart', 'regular', 'price'])}
                      coreCharge={product.getIn(['cart', 'core_charge'])}
                      qty={product.getIn(['cart', 'qty'])}
                      number={product.get('product_id')}
                      name={product.get('product_name')}
                      href={'/cart/ViewItem/' + product.get('product_id')}
                      changePage={p => { document.location.href = p; }}
                      {...special}
                    />
                  );
                })}
              </ProductTable>
              <div className="row">
                <div className="col-sm-12">
                  {this.renderTotalSavings(cart.get('savings'))}
                  <StockStatus show={auth.session_data.admin} inStock={cart.get('in_stock')} />
                  <LocalUsePick />
                </div>
              </div>
            </div>
          </div>
          <div className='row'>
            <Render if={this.props.accountCookiePresent}>
              <CartHistory
                data={this.props.cart_history}
                meta={this.props.getCartHistoryMeta}
                screenWidth={this.props.width}
                discountLevel={this.props.discount_level}
                addProductToCart={this.updateCart}
                addProductToCartMeta={{ success: !cart.get('productLoading'), failure: null, loading: cart.get('productLoading'), reset: () => {} }}
                hideCartLink={true}
              />
            </Render>
          </div>
        </div>
      </div>
    )
  }
}

const LocalUsePick = ({ show }) => {
  const canCreateLocalPicks = usePermission('create-local-picks');

  if (!canCreateLocalPicks) {
    return null;
  }

  return <Button variant="contained" color="primary" label="Local Use Pick" onClick={() => window.location.href = '/admin_local_request.php'}>Local Use Pick</Button>
}

const StockStatus = ({ show, inStock }) => {
  if (!show) {
    return null;
  }

  if (inStock) {
    return <div style={{ fontWeight: 'bold', margin: '15px 0' }}>At this moment, we have the part(s) in stock to fill this order.</div>;
  }

  return <div style={{ color: 'red', margin: '15px 0' }}>We do not currently have the part(s) in stock to fill this order.</div>;
}

const AdminButtons = (props) => {
  if (props.auth.session_data.admin) {
    return (
      <div className='row'>
        <div className='col-sm-8'>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              props.setPurchaseTakenBy(props.auth.session_data.user?.email);
              props.goToCheckout(true, props.changePage);
            }}
            style={{width: '100%', marginTop: 15, marginBottom: 5}}
          >
            Admin Checkout
            <ChevronRight />
          </Button>
        </div>
        <div className='col-sm-4'>
          <SaveQuotePopup save={props.saveQuote} nameRequired={true} />
        </div>
      </div>
    );
  }
  return null;
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(globalStyles)(CartRouter));